import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Stack, Typography } from '@mui/material'
import { Button, Input, MenuButton, Switch, Card, Alert } from '@repo/ui'

import { useColorMode } from 'hooks'
import { useSnackbar } from 'contexts'
import { AppearanceToggle, StatusChip, SelectField } from 'components'

import { NarrationRespendDemo } from '../../GtmArticlesPage/NarrationRespendDemo'
import { LabsSideColumn } from '../components/LabsSideColumn'
import { LABS_CONTENT_TOP } from '../constants'
import { zoomOf } from 'utils/textZoom'

/**
 * Gap between the two columns — 32px, the same `COL_GAP` register `NotificationsLab` uses,
 * so the two Labs sections sit on one rhythm rather than each choosing its own.
 */
const COL_GAP = 8

const SELECT_ITEMS = [
  { label: 'Option A', value: 'a' },
  { label: 'Option B', value: 'b' },
  { label: 'Option C', value: 'c' },
]

// Sample StatusChip status map — exercises the VERA-360 tinted treatment in both modes.
const SAMPLE_STATUSES = {
  active: { label: 'Active', color: '#16a34a', backgroundColor: 'rgba(22,163,74,0.1)', borderColor: '#86efac' },
  pending: { label: 'Pending', color: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)', borderColor: '#fcd34d' },
  suspended: { label: 'Suspended', color: '#dc2626', backgroundColor: 'rgba(220,38,38,0.1)', borderColor: '#fca5a5' },
}

const ALERT_SEVERITIES = ['success', 'info', 'warning', 'error'] as const

interface Specimen {
  id: string
  name: string
  /** Which layer this belongs to — the left column's scope filter. */
  group: string
  /**
   * The component's own directory in this repo. This is what the specimen's CATEGORY is
   * derived from — see `categoryOf` — and it doubles as the answer to "where does this
   * actually live", which is the question a gallery of components is most often opened to
   * settle.
   */
  source: string
  /**
   * Category, ONLY for a specimen whose `source` has no `components/<category>/` segment
   * to read. Every entry using this must say why in a comment; it is an escape hatch for
   * a component that does not live in the design system's own directory structure, not a
   * way to overrule the derivation.
   */
  category?: string
  render: () => React.ReactNode
}

interface Group {
  key: string
  label: string
  source: string
  empty?: string
}

const GROUPS: Group[] = [
  { key: 'common', label: 'Common', source: '@repo/ui — the shared design system' },
  { key: 'platform', label: 'Platform', source: 'apps/platform — built on @repo/ui' },
  {
    key: 'app',
    label: 'App',
    source: 'apps/app — built on @repo/ui',
    empty:
      'No app-layer components yet. apps/app has no shared component library, and its components live in a separate build the platform cannot import. When a shared app library exists (or a per-app Labs is added), its components — also consuming @repo/ui — surface here.',
  },
]

/** The layer scope, as the left column's segmented filter. See `LabsSideColumn`. */
const SCOPE_OPTIONS = GROUPS.map(g => ({ label: g.label, value: g.key }))

/**
 * Display names for the design system's own top-level component directories.
 *
 * The map exists so a directory can be renamed or added without every specimen changing,
 * and so an UNKNOWN directory is visible rather than silently mis-labelled: anything not
 * listed falls through to its raw segment, capitalised, which reads as obviously
 * un-curated the first time someone sees it.
 */
const CATEGORY_LABELS: Record<string, string> = {
  display: 'Display',
  feedback: 'Feedback',
  input: 'Input',
  navigation: 'Navigation',
  layout: 'Layout',
}

/**
 * A specimen's category, DERIVED from where its component actually lives (VRG-560).
 *
 * ## Why derive rather than hand-label
 *
 * The ask was to group the gallery the way the design system groups itself, and the design
 * system's grouping is not written down anywhere as a list — it IS the directory
 * structure: `packages/ui/components/{display,feedback,input,navigation}` and the same
 * four under `apps/platform/src/components`. A hand-kept `category` field would be a
 * second, unverifiable copy of that structure, free to drift the moment a component moves
 * between directories — and nothing would report it, because a gallery entry filed under
 * the wrong heading still renders.
 *
 * ## The honest limit, stated rather than papered over
 *
 * This does NOT read the filesystem. The specimens are a hand-authored array — they have
 * to be, because each one is a bespoke render function, not something a build step can
 * enumerate — so `source` is a string an author types. What derivation buys is therefore
 * narrower than it sounds: it makes the category a pure function of a path that is itself
 * checkable by opening it, instead of a free-text label that is checkable against nothing.
 * A wrong path is still possible; a path that is right while the category is wrong is not.
 *
 * A specimen whose component genuinely lives outside a `components/<category>/` directory
 * sets `category` explicitly with a comment. Today exactly one does.
 */
const categoryOf = (s: Specimen): string => {
  if (s.category) return s.category
  const m = s.source.match(/(?:^|\/)components\/([^/]+)(?:\/|$)/)
  if (!m) return 'Other'
  return CATEGORY_LABELS[m[1]] ?? m[1].charAt(0).toUpperCase() + m[1].slice(1)
}

// rAF tween — this scroll container ignores native `behavior:'smooth'`, so we
// animate scrollTop ourselves. A setTimeout fallback snaps to the target if rAF
// is throttled (e.g. an embedded preview pane), so the jump always lands.
const animateScrollTo = (scroller: HTMLElement, target: number, duration = 320) => {
  const start = scroller.scrollTop
  if (Math.abs(target - start) < 1) return
  const change = target - start
  const t0 = performance.now()
  let done = false
  const ease = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)
  const step = (now: number) => {
    if (done) return
    const p = Math.min(1, (now - t0) / duration)
    scroller.scrollTop = start + change * ease(p)
    if (p < 1) requestAnimationFrame(step)
    else done = true
  }
  requestAnimationFrame(step)
  setTimeout(() => {
    if (!done && scroller.scrollTop === start) {
      scroller.scrollTop = target
      done = true
    }
  }, 80)
}

/**
 * Component lab — in-app gallery of the design system.
 *
 * ## The layout, and what changed in VRG-560
 *
 * Two columns: a left index (layer scope → search → the specimens grouped by category)
 * and the gallery itself. It was a full-width sticky BAND — a Common|Platform|App tab row,
 * a search + Dark row, and a horizontal scroll-spy strip of pills — over a single column.
 *
 * Three things that arrangement got wrong, in descending order of how much they mattered:
 *
 * 1. **The pill strip could not hold the index.** It was one horizontal scrolling row, so
 *    a category heading was impossible (there is no second axis to hang one on) and long
 *    names were reachable only by scrolling sideways through them. A vertical rail has
 *    room for both, which is why `NotificationsLab` already moved its own index into a
 *    column, and why this one now shares that component rather than copying it (§36).
 * 2. **The scope row was a filter dressed as navigation.** See `LabsSideColumn`.
 * 3. **It cost a second sticky level**, and its own scroll-spy had to subtract the
 *    combined height of both to find its trigger line. See `LABS_CONTENT_TOP`.
 *
 * The Dark control did NOT move into the column with the tabs — it is not a filter on the
 * index, it flips the theme the specimens render in, so it sits in the gallery's own
 * header beside the thing it changes.
 */
export const ComponentsLab = () => {
  const { resolvedMode, toggle } = useColorMode()
  const { open } = useSnackbar()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selected, setSelected] = useState<{ label: string; value: any }>(SELECT_ITEMS[0])
  const [field, setField] = useState('a')
  const [checked, setChecked] = useState(true)
  const [activeGroup, setActiveGroup] = useState(() => {
    const g = searchParams.get('group')
    return GROUPS.some(x => x.key === g) ? (g as string) : 'common'
  })
  const [search, setSearch] = useState('')
  const [activeId, setActiveId] = useState<string>('')

  const rootRef = useRef<HTMLDivElement>(null)
  /**
   * The left column, and the only thing this section measures.
   *
   * The scroll-spy needs the bottom edge of whatever is occluding the top of the
   * scrollport. That used to be this section's own sticky band, so the band was the thing
   * measured; with the band gone the occluder is the page header alone, which is a SIBLING
   * this component has no ref to.
   *
   * Rather than reach for the published `--labs-header-h` and convert it, measure the
   * column: it pins at exactly `LABS_CONTENT_TOP`, i.e. the header's bottom, so its own
   * `top` IS the bottom of the occluded strip once pinned — and at scroll 0 it is the same
   * line anyway, because the header block's `pb` sits inside the measured header. One
   * measurement of a real element, which is what `jumpTo` and the old band-based version
   * both already did.
   */
  const tocRef = useRef<HTMLDivElement>(null)

  // All specimens, tagged by layer and by source path. Built here so render fns close over
  // state. ORDER IS NOT MEANINGFUL — the list is sorted by category then name before it is
  // rendered, so a new entry can go anywhere.
  const allSpecimens: Specimen[] = useMemo(
    () => [
      { id: 'c-button-variants', name: 'Button — variants', group: 'common', source: 'packages/ui/components/input/Button', render: () => (
        <>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="tertiary">Tertiary</Button>
          <Button variant="text">Text</Button>
          <Button variant="primary" destructive>Destructive</Button>
          <Button variant="primary" loading>Loading</Button>
          <Button variant="primary" disabled>Disabled</Button>
        </>
      ) },
      { id: 'c-button-sizes', name: 'Button — sizes', group: 'common', source: 'packages/ui/components/input/Button', render: () => (
        <>
          <Button variant="primary" size="x-small">X-Small</Button>
          <Button variant="primary" size="small">Small</Button>
          <Button variant="primary" size="medium">Medium</Button>
          <Button variant="primary" size="large">Large</Button>
        </>
      ) },
      { id: 'c-switch', name: 'Switch', group: 'common', source: 'packages/ui/components/input/Switch', render: () => (
        <>
          <Switch checked={checked} onChange={(_e, v) => setChecked(v)} />
          <Switch checked={false} />
          <Switch checked disabled />
        </>
      ) },
      { id: 'c-input', name: 'Input', group: 'common', source: 'packages/ui/components/input/Input', render: () => (
        <>
          <Box sx={{ width: 240 }}><Input label="Label" placeholder="Placeholder" /></Box>
          <Box sx={{ width: 240 }}><Input label="Error" error helperText="Something's wrong" defaultValue="bad value" /></Box>
          <Box sx={{ width: 240 }}><Input label="Disabled" disabled placeholder="Disabled" /></Box>
        </>
      ) },
      { id: 'c-select', name: 'Select', group: 'common', source: 'packages/ui/components/input/MenuButton', render: () => (
        <MenuButton
          items={SELECT_ITEMS}
          selected={selected}
          variant="secondary"
          onSelect={(v: any) =>
            setSelected(v && typeof v === 'object' && 'label' in v ? v : SELECT_ITEMS.find(i => i.value === v) ?? SELECT_ITEMS[0])
          }
        />
      ) },
      { id: 'c-alert-outlined', name: 'Alert — outlined', group: 'common', source: 'packages/ui/components/feedback/Alert', render: () => (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          {ALERT_SEVERITIES.map(s => (
            <Alert key={s} severity={s} variant="outlined" title={s[0].toUpperCase() + s.slice(1)} description={`This is an outlined ${s} alert.`} />
          ))}
        </Stack>
      ) },
      { id: 'c-alert-filled', name: 'Alert — filled', group: 'common', source: 'packages/ui/components/feedback/Alert', render: () => (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          {ALERT_SEVERITIES.map(s => (
            <Alert key={s} severity={s} variant="filled" title={s[0].toUpperCase() + s.slice(1)} description={`This is a filled ${s} alert.`} />
          ))}
        </Stack>
      ) },
      { id: 'p-statuschip', name: 'StatusChip — tinted (VERA-360 surface)', group: 'platform', source: 'apps/platform/src/components/display/StatusChip', render: () => (
        <>
          <StatusChip statuses={SAMPLE_STATUSES} status="active" size="small" />
          <StatusChip statuses={SAMPLE_STATUSES} status="pending" size="small" />
          <StatusChip statuses={SAMPLE_STATUSES} status="suspended" size="small" />
        </>
      ) },
      { id: 'p-selectfield', name: 'SelectField', group: 'platform', source: 'apps/platform/src/components/input/fields/SelectField.tsx', render: () => (
        <Box sx={{ width: 240 }}><SelectField label="Field" options={SELECT_ITEMS} value={field} onChange={setField} /></Box>
      ) },
      {
        // VRG-739 — the narration re-spend gate. Shown here until an article editor exists to
        // open it from a real save. The plan is computed; nothing is sent.
        id: 'p-narration-respend',
        name: 'Narration re-spend confirmation (VRG-739)',
        group: 'platform',
        source: 'apps/platform/src/pages/internal/GtmArticlesPage/NarrationRespendModal.tsx',
        // A page-level modal: no `components/<category>/` segment for `categoryOf` to read.
        category: 'Feedback',
        render: () => <NarrationRespendDemo />,
      },
      {
        id: 'p-toast',
        name: 'Toast / Snackbar (VERA-360 surface)',
        group: 'platform',
        // The one explicit category on this list. The toast is not a component in
        // `components/` at all — it is rendered by a CONTEXT PROVIDER, so there is no
        // `components/<category>/` segment for `categoryOf` to read. It is filed under
        // Feedback because that is where a toast would live if it were a component, and
        // where a reader looking for it will check first.
        source: 'apps/platform/src/contexts/SnackbarContext',
        category: 'Feedback',
        render: () => (
          <>
            <Button variant="secondary" onClick={() => open({ type: 'success', title: 'Saved', text: 'Your changes were saved.' })}>Success</Button>
            <Button variant="secondary" onClick={() => open({ type: 'error', title: 'Error', text: 'Something went wrong.' })}>Error</Button>
            <Button variant="secondary" onClick={() => open({ type: 'warning', title: 'Warning', text: 'Heads up — check this.' })}>Warning</Button>
            <Button variant="secondary" onClick={() => open({ type: 'info', title: 'Info', text: 'For your information.' })}>Info</Button>
          </>
        ),
      },
    ],
    [checked, selected, field, open],
  )

  const query = search.trim().toLowerCase()

  // Visible set: a search spans every layer; otherwise the active scope. Same rule as the
  // Notifications index, so the two columns behave identically.
  const visibleSpecimens = useMemo(
    () =>
      query
        ? allSpecimens.filter(s => s.name.toLowerCase().includes(query))
        : allSpecimens.filter(s => s.group === activeGroup),
    [allSpecimens, query, activeGroup],
  )

  /**
   * Category groups, alphabetical, alphabetical by name within each (VRG-560).
   *
   * The gallery was previously in whatever order the array happened to be written in,
   * which is fine at ten specimens and useless at fifty — and a gallery's whole job is
   * "show me what exists", which nobody can answer from an arbitrary order.
   *
   * `localeCompare` on both axes, matching `NotificationsLab`'s grouping exactly. The
   * rendered order and the TOC order are the SAME array, which is what lets the scroll-spy
   * and the rail agree without either one re-deriving it.
   */
  const grouped = useMemo(() => {
    const byCat = new Map<string, Specimen[]>()
    for (const s of visibleSpecimens) {
      const c = categoryOf(s)
      if (!byCat.has(c)) byCat.set(c, [])
      byCat.get(c)!.push(s)
    }
    return Array.from(byCat.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, items]) => ({ category, items: items.sort((a, b) => a.name.localeCompare(b.name)) }))
  }, [visibleSpecimens])

  /** The flattened render order — exactly what the rail shows, top to bottom. */
  const ordered = useMemo(() => grouped.flatMap(g => g.items), [grouped])
  const activeGroupMeta = GROUPS.find(g => g.key === activeGroup) ?? GROUPS[0]

  // Walk up from an in-DOM node to its scroll container (the Layout <main> box).
  const getScroller = (from: HTMLElement | null): HTMLElement | null => {
    let el: HTMLElement | null = from
    while (el) {
      const oy = getComputedStyle(el).overflowY
      if (oy === 'auto' || oy === 'scroll') return el
      el = el.parentElement
    }
    return null
  }

  /**
   * The occluded strip's height: the left column's pinned top, in scroller coordinates —
   * i.e. LAYOUT px, the space `scrollTop` and `clientHeight` live in. The two rects are
   * PAINTED px (× the text zoom), so their difference is divided back out; see `zoomOf`.
   */
  const occludedHeight = (scroller: HTMLElement) => {
    const top = tocRef.current?.getBoundingClientRect().top
    if (top == null) return 0
    return Math.max(0, Math.round((top - scroller.getBoundingClientRect().top) / zoomOf(scroller)))
  }

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const scroller = getScroller(el)
    if (!scroller) return
    setActiveId(id) // highlight immediately on click; scroll-spy keeps it in sync
    // `scrollTop` is layout px and the rect delta is painted px, so the delta is divided by
    // the scroller's text zoom before the two are added. Unconverted, the jump landed short
    // by delta × (1 − z) at z < 1 and overshot at z > 1.
    const delta = (el.getBoundingClientRect().top - scroller.getBoundingClientRect().top) / zoomOf(scroller)
    const target = scroller.scrollTop + delta - occludedHeight(scroller) - 12
    animateScrollTo(scroller, Math.max(0, target))
  }

  const selectGroup = (key: string) => {
    setSearch('')
    setActiveGroup(key)
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev)
        p.set('group', key)
        return p
      },
      { replace: true },
    )
    const scroller = getScroller(rootRef.current)
    if (scroller) animateScrollTo(scroller, 0)
  }

  /**
   * Scroll-spy via IntersectionObserver — a trigger band just below the occluded strip
   * marks the active specimen. (An observer rather than a `scroll` listener because this
   * container does not emit scroll events on programmatic scrolls, which is how `jumpTo`
   * moves.)
   *
   * ## The `-80%` bottom margin was degenerate, and this is a fix, not a port (VRG-560)
   *
   * The band was `rootMargin: -${headerH}px 0px -80% 0px`. Percentages in `rootMargin`
   * resolve against the ROOT's own dimensions, so `-80%` puts the band's bottom edge at
   * 20% of the scrollport height — **160px on an 800px window** — while its top edge is
   * the occluded strip, measured here at **178px**. Top below bottom: the band has height
   * **-18px**, nothing can ever intersect it, `inBand` is always empty, and the callback
   * returns without ever setting `activeId`.
   *
   * Measured, not inferred: an observer built with the old margin and pointed at all seven
   * specimens over a full scroll sweep recorded **zero** intersections.
   *
   * This is PRE-EXISTING and was not introduced by deleting the band — deleting the band
   * made the occluded strip SMALLER (it was header + band, now header alone), so the old
   * code was strictly more degenerate. What makes it a viewport bug rather than a constant
   * one is the percentage: the band is only valid while `0.2 * H > occluded`, i.e. above a
   * window height of about **890px**. So it worked on a tall monitor and was silently dead
   * on every laptop, which is exactly why nobody filed it.
   *
   * The bottom margin is therefore computed in PIXELS from the root's measured height, so
   * the band is a fixed `TRIGGER_H` tall wherever the window is, and `Math.max(0, …)`
   * keeps it valid on a window too short to hold one.
   *
   * Re-run on resize for the same reason the margin is not a percentage: the root's height
   * is an input, and an input measured once is an input that goes stale — the defect this
   * whole folder keeps re-fixing.
   */
  useEffect(() => {
    if (!ordered.length) return
    const first = document.getElementById(ordered[0].id)
    const scroller = getScroller(first)
    if (!scroller) return

    let io: IntersectionObserver | null = null
    const attach = () => {
      io?.disconnect()
      // The whole occluded strip, read off the DOM rather than summed from published
      // numbers — the same measurement `jumpTo` makes.
      const occluded = occludedHeight(scroller)
      const TRIGGER_H = 120
      const bottom = Math.max(0, scroller.clientHeight - occluded - TRIGGER_H)
      io = new IntersectionObserver(
        entries => {
          const inBand = entries.filter(e => e.isIntersecting)
          if (!inBand.length) return
          const topmost = inBand.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
          setActiveId((topmost.target as HTMLElement).id)
        },
        { root: scroller, rootMargin: `-${occluded}px 0px -${bottom}px 0px`, threshold: 0 },
      )
      ordered.forEach(s => {
        const n = document.getElementById(s.id)
        if (n) io!.observe(n)
      })
    }

    attach()
    setActiveId(ordered[0].id)
    window.addEventListener('resize', attach)
    return () => {
      io?.disconnect()
      window.removeEventListener('resize', attach)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordered.map(s => s.id).join('|')])

  return (
    <Box ref={rootRef}>
      {/* No `pt` on this row — the gap above it is the page header block's own `pb`
          (`LABS_HEADER_GAP`). Adding one puts the left column's rest position below its
          pinned position and it travels that distance on the first scroll. */}
      <Stack direction="row" spacing={COL_GAP} sx={{ alignItems: 'flex-start' }}>
        <LabsSideColumn
          containerRef={tocRef}
          scope={{ options: SCOPE_OPTIONS, value: activeGroup, onChange: selectGroup }}
          search={{ value: search, onChange: setSearch, placeholder: 'Search components' }}
          groups={grouped.map(({ category, items }) => ({
            category,
            items: items.map(s => ({ label: s.name, value: s.id })),
          }))}
          activeValue={activeId}
          onSelect={jumpTo}
          emptyText={query ? 'No components match your search.' : 'No components in this group yet.'}
        />

        {/* ── The gallery ─────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* The Dark control lives HERE, not in the left column, because it does not
              filter the index — it flips the theme the specimens below render in, which
              is the single reason this section exists (the VERA-360 regression surface).

              It was a bare `Switch` labelled "Dark" in the deleted band. A switch is a
              boolean affordance; this is a choice between two renderings, neither of which
              is the "off" one. `AppearanceToggle` is the product's existing control for
              exactly that (§36) — unlike the Notifications lab's copy, this one drives the
              app-wide `useColorMode`, so clicking the active side is a deliberate no-op:
              the hook exposes a `toggle()`, not a setter. */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={4} sx={{ mb: 4, minHeight: 48 }}>
            <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ minWidth: 0 }}>
              <Typography variant="h6">{query ? 'Search results' : activeGroupMeta.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {query ? `${ordered.length} match${ordered.length === 1 ? '' : 'es'}` : activeGroupMeta.source}
              </Typography>
            </Stack>
            <AppearanceToggle
              value={resolvedMode === 'dark' ? 'dark' : 'light'}
              onChange={next => {
                if (next !== resolvedMode) toggle()
              }}
            />
          </Stack>

          {ordered.length ? (
            /* Rendered from `grouped`, not from a flat list, so the gallery carries the
               same category headings the rail does — a specimen's position on screen and
               its position in the index are the same fact, stated once. */
            <Stack spacing={6}>
              {grouped.map(({ category, items }) => (
                <Box key={category}>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                    {category}
                  </Typography>
                  <Stack spacing={4}>
                    {items.map(s => (
                      // `scrollMarginTop` is the page header's published height, so a
                      // native anchor jump lands clear of it. `jumpTo` does its own
                      // arithmetic and does not rely on this.
                      <Box key={s.id} id={s.id} sx={{ scrollMarginTop: LABS_CONTENT_TOP }}>
                        <Card>
                          <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">
                              {s.name}
                            </Typography>
                            {/* The path the category was derived from. It answers "where
                                does this live" without a trip to the file tree, and it is
                                what makes a mis-filed specimen visible: a heading and a
                                path that disagree are readable side by side. */}
                            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                              {s.source}
                            </Typography>
                          </Stack>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            {s.render()}
                          </Box>
                        </Card>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ border: theme => `1px dashed ${theme.palette.divider}`, borderRadius: 2, p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {query ? 'No components match your search.' : activeGroupMeta.empty}
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  )
}
